<template>
  <component
    :is="isLink ? TheNuxtLink : 'button'"
    v-bind="isLink ? linkProps : buttonProps"
    :class="contextClassName"
    class="button inline-block cursor-pointer truncate rounded border border-solid px-2 py-[0.375rem] text-center text-base leading-6 tablet:py-[0.1875rem]"
  >
    <slot />
  </component>
</template>

<!-- eslint-disable @typescript-eslint/no-unused-vars -->
<script setup lang="ts">
  import type { RouteLocationRaw } from 'vue-router';

  import type { NuxtLinkProps } from '#app';

  /**
   * * Desktop & Tablet 的按鈕高度：
   * * 32px = 24px(line-height) + 6px(padding) + 2px(border)
   *
   * * Mobile 的按鈕高度：
   * * 38px = 24px(line-height) + 12px(padding) + 2px(border)
   */

  type TButtonType =
    | 'primary'
    | 'default'
    | 'outline'
    | 'orange'
    | 'transparent'
    | 'freeTrial';

  interface IProps extends NuxtLinkProps {
    to?: RouteLocationRaw;
    title?: string;
    type?: TButtonType;
    disabled?: boolean;
  }

  const props = withDefaults(defineProps<IProps>(), {
    to: undefined,
    title: undefined,
    type: 'primary',
    disabled: false,
  });

  const TheNuxtLink = resolveComponent('TheNuxtLink');

  const isLink = computed((): boolean => props.to !== undefined);

  const linkProps = computed((): NuxtLinkProps => {
    const { type, disabled, ...nuxtLinkProps } = props;
    return nuxtLinkProps;
  });

  const buttonProps = computed(() => ({
    type: 'button',
    disabled: props.disabled,
    title: props.title,
  }));

  const contextClassName = computed((): string => {
    if (props.disabled) {
      return 'bg-gray-300 border-gray-300 text-gray-400 pointer-events-none';
    }

    switch (props.type) {
      case 'default':
        return `
        bg-white border-gray-400 text-gray-600
        active:border-blue-600 active:bg-blue-600 active:text-white active:underline
        hover:border-blue-400 hover:bg-blue-400 hover:text-white hover:underline
        `;
      case 'outline':
        return `
        bg-white border-blue-400 text-blue-400
        active:border-blue-600 active:bg-blue-600 active:text-white active:underline
        hover:border-blue-400 hover:bg-blue-400 hover:text-white hover:underline`;
      case 'orange':
        return `
        bg-orange-400 border-orange-400 text-white
        active:border-orange-600 active:bg-orange-600 active:text-white active:underline
        hover:border-orange-400 hover:bg-orange-400 hover:text-white hover:underline`;
      case 'transparent':
        return `
        bg-transparent border-white text-white active:underline hover:underline`;
      case 'freeTrial':
        return `
        border-orange-400 text-orange-400
        active:border-orange-500 active:bg-orange-100 active:text-orange-500 active:underline
        hover:border-orange-400 hover:bg-orange-100 hover:text-orange-400 hover:underline`;
      case 'primary':
      default:
        return `
        bg-blue-400 border-blue-400 text-white
        active:border-blue-600 active:bg-blue-600 active:text-white active:underline
        hover:border-blue-400 hover:bg-blue-400 hover:text-white hover:underline`;
    }
  });

  export type { TButtonType };
</script>

<style lang="scss" scoped>
  .button:deep(span) {
    @apply align-top leading-6;
  }
</style>
